import React from 'react';
import { Box, Button, Container, Divider, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Mudou o texto? Troque a versão: todo usuário precisa aceitar de novo no próximo acesso.
export const TERMS_VERSION = '2026-10-14';

const SECTIONS = [
  {
    title: '1. Aceite',
    body: [
      'Ao entrar no Entertainment Maintenance ("Sistema") você declara que leu e concorda com estes Termos de Uso e com a Política de Privacidade abaixo. Se não concordar, não use o Sistema.'
    ]
  },
  {
    title: '2. O serviço',
    body: [
      'O Sistema ajuda donos e equipes de lojas de máquinas de entretenimento a acompanhar máquinas, chamados, manutenções, estoque de peças e faturamento.',
      'O acesso aos dados de uma loja depende de vínculo liberado pelo proprietário da loja ou pela nossa equipe. Cada usuário só enxerga as lojas às quais está vinculado.'
    ]
  },
  {
    title: '3. Conta e acesso',
    body: [
      'O acesso é pessoal e intransferível. Você é responsável por manter sua conta (Google ou e-mail) segura e por tudo o que for feito com ela.',
      'Compartilhar o acesso, usar a conta de outra pessoa ou tentar acessar dados de lojas às quais você não está vinculado é proibido e leva ao bloqueio imediato.'
    ]
  },
  {
    title: '4. Modo demonstração',
    body: [
      'Quem entra sem vínculo com uma loja usa o modo demonstração: uma loja fictícia, com dados inventados, apenas para leitura. Nenhum dado real de cliente é exibido nesse modo.',
      'O modo demonstração serve exclusivamente para avaliar a contratação do serviço.'
    ]
  },
  {
    title: '5. Propriedade intelectual',
    body: [
      'O Sistema, incluindo telas, fluxos, textos, marca, código-fonte, banco de dados e a forma de organizar as informações, pertence aos seus criadores e é protegido pela Lei de Direitos Autorais (Lei 9.610/98) e pela Lei de Software (Lei 9.609/98).',
      'O uso do Sistema não transfere nenhum direito sobre ele. Você recebe apenas uma licença pessoal, limitada, revogável e não exclusiva para usá-lo conforme estes Termos.'
    ]
  },
  {
    title: '6. Usos proibidos',
    body: [
      'É proibido, inclusive no modo demonstração:',
      '• copiar, reproduzir ou imitar o Sistema, suas telas ou funcionalidades, no todo ou em parte;',
      '• fazer engenharia reversa, descompilar ou tentar obter o código-fonte;',
      '• coletar dados de forma automatizada (robôs, scripts, "scraping") ou acessar o Sistema por meios que não sejam a interface oferecida;',
      '• usar o Sistema, ou informações obtidas nele, para criar, melhorar ou divulgar produto ou serviço concorrente;',
      '• contornar, testar ou desativar mecanismos de segurança e de controle de acesso;',
      '• revender, sublicenciar ou disponibilizar o acesso a terceiros.',
      'O descumprimento leva ao bloqueio do acesso e pode gerar responsabilização civil e criminal, incluindo indenização por perdas e danos.'
    ]
  },
  {
    title: '7. Privacidade e proteção de dados (LGPD)',
    body: [
      'Dados que coletamos: nome, e-mail e foto da sua conta Google (ou o e-mail usado no login); as lojas e cargos aos quais você está vinculado; e o registro do que você faz no Sistema — telas acessadas, chamados, manutenções, movimentações e alterações, com data e hora.',
      'Para que usamos: prestar o serviço contratado; garantir a segurança e o controle de acesso; manter o histórico e a auditoria das lojas; e, no modo demonstração, entender o interesse no produto e entrar em contato comercial.',
      'Bases legais (art. 7º da Lei 13.709/18): execução de contrato e procedimentos preliminares a ele, cumprimento de obrigação legal e legítimo interesse (segurança e prevenção a fraudes).',
      'Com quem compartilhamos: apenas com fornecedores necessários para o Sistema funcionar — hospedagem e banco de dados (Supabase), armazenamento de arquivos (Cloudflare), autenticação (Google) e envio de e-mails (Resend). Não vendemos seus dados.',
      'Por quanto tempo: enquanto houver conta ou vínculo ativo e, depois disso, pelo tempo necessário para auditoria e obrigações legais. O histórico do que foi feito em uma loja (quem abriu, atendeu ou alterou cada registro) permanece no registro da loja mesmo depois que o usuário é desvinculado dela.',
      'Seus direitos (art. 18 da LGPD): confirmar o tratamento, acessar, corrigir, pedir anonimização ou eliminação de dados desnecessários, pedir portabilidade, saber com quem os dados são compartilhados e revogar consentimento. Para exercê-los, fale com a gente pelo canal de Ajuda do Sistema.'
    ]
  },
  {
    title: '8. Disponibilidade e responsabilidade',
    body: [
      'Trabalhamos para manter o Sistema disponível e seguro, mas ele pode passar por manutenções e instabilidades. As informações exibidas apoiam a gestão da loja e não substituem a avaliação técnica das máquinas.'
    ]
  },
  {
    title: '9. Alterações destes Termos',
    body: [
      'Podemos atualizar estes Termos. Quando isso acontecer, pediremos um novo aceite no seu próximo acesso.'
    ]
  },
  {
    title: '10. Lei e foro',
    body: [
      'Estes Termos seguem as leis brasileiras. Fica eleito o foro da comarca de São Paulo/SP para resolver qualquer questão relacionada a eles.'
    ]
  }
];

// /privacidade: só a seção de privacidade (link exigido pela tela de consentimento do Google)
const PRIVACY_SECTIONS = [
  { ...SECTIONS.find((s) => s.title.startsWith('7.')), title: 'Privacidade e proteção de dados (LGPD)' },
  {
    title: 'Dados da sua conta Google',
    body: [
      'Ao entrar com o Google, recebemos apenas seu nome, e-mail e foto de perfil, usados só para identificar você no Sistema. Não acessamos e-mails, arquivos, contatos ou qualquer outro dado da sua conta Google, e não usamos esses dados para publicidade.'
    ]
  }
];

export default function TermsPage({ privacy = false }) {
  const sections = privacy ? PRIVACY_SECTIONS : SECTIONS;
  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh', py: { xs: 4, sm: 6 } }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{privacy ? 'Política de Privacidade' : 'Termos de Uso e Política de Privacidade'}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Versão de {TERMS_VERSION.split('-').reverse().join('/')}</Typography>
        <Divider sx={{ my: 3 }} />
        {sections.map((s) => (
          <Box key={s.title} component="section" sx={{ mb: 3.5 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>{s.title}</Typography>
            {s.body.map((p) => (
              <Typography key={p} variant="body1" sx={{ mb: 1, lineHeight: 1.7 }}>{p}</Typography>
            ))}
          </Box>
        ))}
        <Button component={RouterLink} to="/" variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>Voltar</Button>
      </Container>
    </Box>
  );
}
